import React, { useState } from 'react';
import { Form, InputNumber, Switch, Alert, Flex, Typography, Statistic, Card, Empty, Divider } from 'antd';
import { LineChartOutlined } from '@ant-design/icons';
import GreenLightButton from '../../../components/UI/Buttons/GreenLightButton';
import LspivService from '../../../services/LspivService';
import { clrs } from '../../../constants/colors';

const { Title, Text } = Typography;

const ResultsStep = ({ jobId, onRefresh }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const compute = async () => {
        const values = await form.validateFields();
        setError(null); setLoading(true); setResult(null);
        try {
            const r = await LspivService.computeDischarge(jobId, {
                step: values.step,
                fps: values.fps,
                id_section: values.id_section,
                interpolate: values.interpolate,
                artificial_seeding: values.artificial_seeding,
                alpha: values.alpha,
                num_stations: values.num_stations,
            });
            setResult(r);
            await onRefresh();
        } catch (e) {
            setError(e?.response?.data?.detail || 'Ошибка расчёта расхода');
        } finally { setLoading(false); }
    };

    // извлекаем расход и среднюю скорость из первого обновлённого сечения
    const extractStats = (data) => {
        if (!data) return null;
        const firstKey = Object.keys(data).find((k) => k.startsWith('section_'));
        if (!firstKey) return null;
        const s = data[firstKey];
        return {
            key: firstKey,
            discharge: s?.discharge,
            mean_velocity: s?.mean_velocity,
            width: s?.width,
            area: s?.area,
        };
    };

    const stats = extractStats(result);

    return (
        <Flex vertical gap={20}>
            <Title level={4} style={{ margin: 0 }}>
                <LineChartOutlined /> Расход и результаты
            </Title>
            <Text type="secondary">
                Соберите итог: расход Q, средняя скорость, ширина и площадь сечения — по выбранному
                поперечному сечению на основе PIV-результатов и матрицы ректификации.
            </Text>

            {error && <Alert type="error" message={error} showIcon />}

            <Form form={form} layout="vertical" initialValues={{
                step: 2, fps: 30, id_section: 0, interpolate: false,
                artificial_seeding: false, alpha: 0.85, num_stations: 15,
            }}>
                <Flex gap={12} wrap="wrap">
                    <Form.Item name="step" label="step" rules={[{ required: true }]}><InputNumber min={1} max={128} /></Form.Item>
                    <Form.Item name="fps" label="fps" rules={[{ required: true }]}><InputNumber min={1} max={240} /></Form.Item>
                    <Form.Item name="id_section" label="id_section" rules={[{ required: true }]}><InputNumber min={0} /></Form.Item>
                    <Form.Item name="alpha" label="alpha"><InputNumber min={0.1} max={1} step={0.05} /></Form.Item>
                    <Form.Item name="num_stations" label="num_stations"><InputNumber min={3} max={100} /></Form.Item>
                    <Form.Item name="interpolate" label="interpolate" valuePropName="checked"><Switch /></Form.Item>
                    <Form.Item name="artificial_seeding" label="artificial_seeding" valuePropName="checked"><Switch /></Form.Item>
                </Flex>
                <GreenLightButton onClick={compute} loading={loading}>Рассчитать расход</GreenLightButton>
            </Form>

            <Divider />

            {!result && <Empty description="Результаты появятся после расчёта" />}

            {stats && (
                <Card style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                    <Text type="secondary">Сечение: <b>{stats.key}</b></Text>
                    <Flex gap={40} wrap="wrap" style={{ marginTop: 16 }}>
                        <Statistic
                            title="Расход, Q"
                            value={stats.discharge}
                            precision={3}
                            suffix="м³/с"
                            valueStyle={{ color: clrs.blue, fontWeight: 700, fontSize: '1.8rem' }}
                        />
                        <Statistic
                            title="Средняя скорость"
                            value={stats.mean_velocity}
                            precision={3}
                            suffix="м/с"
                        />
                        <Statistic title="Ширина" value={stats.width} precision={2} suffix="м" />
                        <Statistic title="Площадь" value={stats.area} precision={2} suffix="м²" />
                    </Flex>
                </Card>
            )}

            {result && (
                <Card title="Полный результат (JSON)" size="small">
                    <pre style={{ margin: 0, fontSize: '0.75rem', maxHeight: 400, overflow: 'auto' }}>
                        {JSON.stringify(result, null, 2).slice(0, 5000)}
                    </pre>
                </Card>
            )}
        </Flex>
    );
};

export default ResultsStep;
